import { action, computed, observable, makeAutoObservable } from 'mobx';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';

export type PopupKeyType = string;
export type PopupTemplateType = 'generic'; // extend as needed

export type PopupType = {
    template: PopupTemplateType;
    /** * Enables backdrop for popup */
    hasOverlay: boolean;
    /** * Method to define action on confirm button */
    onConfirm?: () => void;
    /** * Method to define action on delete button */
    onDelete?: () => void;
    /** * Method to define action on cancel button or click outside */
    onCancel?: () => void;
    /** * Popup title */
    title?: string;
    /** * Popup description */
    message?: JSX.Element;
    /** * Label for cancel button */
    cancel_label?: string;
    /** * Label for confirm button */
    confirm_label?: string;
    /** * Label for delete button */
    delete_label?: string;
    /** * To show loader on action */
    isLoading?: boolean;
    /** * Used to add custom styling to the dialog*/
    dialogClassName?: string;
    /** * Used to add custom styling to the popup title*/
    titleClassName?: string;
};

export class PopupsStackState {
    @observable private popupsStack: Map<PopupKeyType, PopupType> = new Map();

    public static get = AutoWeakMap.create((_common) => new PopupsStackState());

    private constructor() {
        makeAutoObservable(this);
    }

    @computed public get popups(): Map<PopupKeyType, PopupType> {
        return this.popupsStack;
    }

    @computed public get hasPopups(): boolean {
        return this.popupsStack.size > 0;
    }

    @computed public get topPopupKey(): string | undefined {
        return [...this.popupsStack.keys()].pop();
    }

    @computed public get topPopup(): PopupType | undefined {
        if (this.topPopupKey === undefined) {
            return undefined;
        } else {
            return this.popupsStack.get(this.topPopupKey);
        }
    }

    @action public addPopup(id: PopupKeyType, popup: PopupType): void {
        this.popupsStack.set(id, popup);
    }

    @action public closePopup(id: PopupKeyType): void {
        this.popupsStack.delete(id);
    }

    @action public closePopupOnTop(): void {
        if (this.topPopupKey !== undefined) {
            this.closePopup(this.topPopupKey);
        }
    }
}
